import { musicMute } from './MusicMute';
import { musicVolume } from './MusicVolume';
import { claimMusicTab, isActiveMusicTab, subscribeMusicTabLock } from './MusicTabLock';

const RACE_TRACK_URLS = [
  '/audio/chrome-alley-strut.mp3',
  '/audio/starship-clavinet-run.mp3',
  '/audio/arcade-alley-funk.mp3',
  '/audio/arcade-star-drive.mp3',
  '/audio/breaking-sweat.mp3',
  '/audio/dust-circuit-run.mp3',
  '/audio/hyperspace-pursuit.mp3',
  '/audio/midnight-star-cruiser.mp3',
  '/audio/tinfoil-thunder.mp3',
  '/audio/wahline-pursuit.mp3',
];
const TARGET_VOLUME = 0.55;
const FADE_IN_MS = 800;
const FADE_OUT_MS = 600;

class RaceMusicPlayer {
  private audio: HTMLAudioElement | null = null;
  private fadeTimer: number | null = null;
  private wantsPlayback = false;
  private currentUrl: string | null = null;

  constructor() {
    musicMute.subscribe((muted) => {
      if (muted) this.pauseAndReset();
      else if (this.wantsPlayback) this.fadeIn();
    });
    musicVolume.subscribe(() => {
      if (this.audio && this.fadeTimer === null) this.audio.volume = this.targetVolume();
    });
    subscribeMusicTabLock(() => {
      if (!isActiveMusicTab()) this.pauseOnly();
      else if (this.wantsPlayback) this.resumeOrFadeIn();
    });
  }

  fadeIn(durationMs = FADE_IN_MS): void {
    this.wantsPlayback = true;
    if (musicMute.isMuted()) return;
    claimMusicTab();
    if (!isActiveMusicTab()) return;
    const audio = this.ensureAudio();
    if (!audio) return;

    this.clearFade();
    audio.volume = 0;
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {
        if (this.wantsPlayback) this.armGestureRetry();
      });
    }

    const start = performance.now();
    const step = (): void => {
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / durationMs);
      audio.volume = this.targetVolume() * t;
      if (t >= 1) {
        this.fadeTimer = null;
        return;
      }
      this.fadeTimer = window.setTimeout(step, 16);
    };
    step();
  }

  fadeOut(durationMs = FADE_OUT_MS): void {
    this.wantsPlayback = false;
    const audio = this.audio;
    if (!audio) return;

    this.clearFade();
    if (audio.paused) {
      audio.currentTime = 0;
      return;
    }

    const start = performance.now();
    const initialVolume = audio.volume;
    const step = (): void => {
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / durationMs);
      audio.volume = initialVolume * (1 - t);
      if (t >= 1) {
        audio.pause();
        audio.currentTime = 0;
        // Pick a fresh track for next race so the player hears variety.
        this.currentUrl = null;
        this.audio = null;
        this.fadeTimer = null;
        return;
      }
      this.fadeTimer = window.setTimeout(step, 16);
    };
    step();
  }

  pause(): void {
    this.pauseOnly();
  }

  resume(): void {
    if (!this.wantsPlayback || musicMute.isMuted()) return;
    claimMusicTab();
    if (!isActiveMusicTab()) return;
    const audio = this.audio;
    if (!audio || !audio.paused) return;
    void audio.play().catch(() => { /* ignore */ });
  }

  stop(): void {
    this.wantsPlayback = false;
    this.clearFade();
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      // Drop the reference so any in-flight gesture-retry, fade-step, or
      // 'ended' handler closure cannot resurrect this player after stop().
      // Next fadeIn() picks a fresh track via ensureAudio().
      this.audio = null;
    }
    this.currentUrl = null;
  }

  private pauseAndReset(): void {
    this.clearFade();
    if (!this.audio) return;
    this.audio.pause();
    this.audio.currentTime = 0;
  }

  private pauseOnly(): void {
    if (!this.audio || this.audio.paused) return;
    this.clearFade();
    this.audio.pause();
  }

  private resumeOrFadeIn(): void {
    if (!this.audio) {
      this.fadeIn();
      return;
    }
    this.resume();
  }

  private ensureAudio(): HTMLAudioElement | null {
    if (this.audio) return this.audio;
    const url = pickTrackUrl(this.currentUrl);
    this.currentUrl = url;
    const audio = new Audio(url);
    audio.loop = false;
    audio.preload = 'auto';
    audio.volume = 0;
    audio.addEventListener('error', () => {
      this.wantsPlayback = false;
    }, { once: true });
    audio.addEventListener('ended', () => this.advanceToNextTrack());
    this.audio = audio;
    return audio;
  }

  private advanceToNextTrack(): void {
    if (!this.wantsPlayback || musicMute.isMuted()) return;
    const nextUrl = pickTrackUrl(this.currentUrl);
    this.currentUrl = nextUrl;
    this.clearFade();
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
    const audio = this.ensureAudio();
    if (!audio) return;
    audio.volume = this.targetVolume();
    void audio.play().catch(() => {
      if (this.wantsPlayback) this.armGestureRetry();
    });
  }

  private armGestureRetry(): void {
    const retry = (): void => {
      window.removeEventListener('pointerdown', retry);
      window.removeEventListener('keydown', retry);
      if (this.wantsPlayback) this.fadeIn();
    };
    window.addEventListener('pointerdown', retry, { once: true });
    window.addEventListener('keydown', retry, { once: true });
  }

  private clearFade(): void {
    if (this.fadeTimer === null) return;
    window.clearTimeout(this.fadeTimer);
    this.fadeTimer = null;
  }

  private targetVolume(): number {
    return TARGET_VOLUME * musicVolume.get();
  }
}

export const raceMusic = new RaceMusicPlayer();

function pickTrackUrl(avoid: string | null): string {
  const pool = avoid && RACE_TRACK_URLS.length > 1
    ? RACE_TRACK_URLS.filter((u) => u !== avoid)
    : RACE_TRACK_URLS;
  return pool[Math.floor(Math.random() * pool.length)] ?? pool[0];
}
